import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ShoppingCart, Star, Palette, Shirt, X } from 'lucide-react';
import About from '../pages/About';
import Contact from '../pages/Contact';
import Hero from './Hero';
import PageTransition from './PageTransition';

const AnimatedRoutes = ({
  baseModels,
  petCategories,
  accessories,
  selectedBase,
  setSelectedBase,
  selectedPet,
  setSelectedPet,
  featuredSectionRef,
  customization,
  setCustomization,
  handleCustomization,
  handleAccessoryToggle,
  addToCart,
  closeModal
}) => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <PageTransition>
            <main className="flex-1 flex flex-col">
              {/* Hero Section */}
              <Hero page="home" />

              {/* Main Content */}
              <div className="flex-1">
                {/* Pet Action Figures Section */}
                <section className="w-full py-8 relative">
                  {/* Gradient background with white center fading to transparent */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"></div>
                  {/* Additional blur/glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-sm"></div>
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <p className="text-xl text-gray-600 text-center mb-2">Escolha seu mascote</p>
                    <div className="flex justify-center gap-4 mb-2">
                      {petCategories.map((category, index) => (
                        <div
                          key={index}
                          className="relative group"
                          onClick={() => {
                            const isDeselecting = selectedPet === category.name;
                            setSelectedPet(isDeselecting ? null : category.name);
                            
                            // Only scroll when selecting (not deselecting)
                            if (!isDeselecting && featuredSectionRef.current) {
                              featuredSectionRef.current.scrollIntoView({ 
                                behavior: 'smooth',
                                block: 'start'
                              });
                            }
                          }}
                        >
                          <div className={`w-32 h-32 flex items-center justify-center cursor-pointer relative ${
                            selectedPet === category.name 
                              ? `after:absolute after:inset-0 after:bg-white after:rounded-full after:border-2 ${
                                  category.name === "Cats" ? "after:border-orange-500" :
                                  category.name === "Dogs" ? "after:border-blue-500" :
                                  "after:border-pink-500"
                                }` 
                              : ''
                          }`}>
                            {/* SVG Shadow Effect */}
                            <svg 
                              className="absolute bottom-2 left-1/2 transform -translate-x-1/2 transition-all duration-300 group-hover:scale-110"
                              width="60" 
                              height="12" 
                              viewBox="0 0 60 12"
                            >
                              <ellipse 
                                cx="30" 
                                cy="6" 
                                rx="25" 
                                ry="4" 
                                fill="rgba(0, 0, 0, 0.15)"
                                className={`transition-all duration-300 ${
                                  selectedPet === category.name ? 'opacity-80' : 'opacity-60 group-hover:opacity-60'
                                }`}
                              />
                            </svg>
                            
                            <div className={`h-20 relative z-10 overflow-hidden transition-all duration-300 ${
                              selectedPet === category.name ? 'scale-95 transition-transform duration-200' : 'group-hover:-translate-y-1'
                            }`} 
                              style={{
                                  width: '4.5rem'
                            }}>
                              <img
                                src={category.icon}
                                alt={category.name}
                                className={`w-full h-auto object-contain ${
                                  selectedPet === category.name ? 'transform -translate-x-0' : 'group-hover:transform group-hover:-translate-x-1/2'
                                }`}
                                style={{
                                  width: '200%',
                                  maxWidth: '200%',
                                  objectPosition: 'left top'
                                }}
                              />
                            </div>
                          </div>
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 transition-opacity duration-300 whitespace-nowrap">
                            <p className={`text-sm font-medium text-white px-4 py-1 rounded-full ${
                              category.name === "Cats" ? "bg-orange-500" :
                              category.name === "Dogs" ? "bg-blue-500" :
                              "bg-pink-500"
                            } ${
                              selectedPet === category.name ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                            }`}>
                              {category.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* Featured Action Figures Section */}
                <section ref={featuredSectionRef} className="py-16 px-4 sm:px-6 lg:px-8">
                  <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-center mb-8">
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">Featured Action Figures</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {baseModels.map((model) => (
                        <div
                          key={model.id}
                          className="bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 cursor-pointer"
                          onClick={() => setSelectedBase(model)}
                        >
                          <div className="relative">
                            <img
                              src={model.image}
                              alt={model.name}
                              className="w-full h-80 object-cover"
                            />
                            <div className="absolute top-4 right-4 bg-white rounded-full px-3 py-1 shadow-lg">
                              <span className="text-yellow-500 flex items-center">
                                <Star className="h-4 w-4 fill-current" />
                                <span className="ml-1 font-semibold">{model.rating}</span>
                              </span>
                            </div>
                          </div>
                          <div className="p-6">
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{model.name}</h4>
                            <p className="text-gray-600 mb-4">{model.description}</p>
                            <div className="flex justify-between items-center">
                              <span className="text-2xl font-bold text-purple-600">${model.price}</span>
                              <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
                                Customize
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </div>

              {/* Customization Modal */}
              {selectedBase && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-2xl font-bold text-gray-900">Customize Your {selectedBase.name}</h3>
                      <button 
                        onClick={closeModal}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <X className="h-6 w-6 text-gray-500" />
                      </button>
                    </div>
                    
                    <div className="p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-gray-50 rounded-2xl p-6">
                          <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Preview</h4>
                          <div className="flex justify-center mb-4">
                            <div className="relative">
                              <img
                                src={selectedBase.image}
                                alt="Custom figure preview"
                                className="w-48 h-60 object-cover rounded-lg shadow-lg"
                                style={{ filter: `hue-rotate(${customization.color === '#3b82f6' ? 0 : 60}deg)` }}
                              />
                              {customization.accessories.map(accessory => {
                                const acc = accessories.find(a => a.id === accessory);
                                return (
                                  <div key={accessory} className="absolute top-2 right-2 text-xl">
                                    {acc?.icon}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="text-center">
                            <input
                              type="text"
                              placeholder="Name your figure"
                              value={customization.name}
                              onChange={(e) => handleCustomization('name', e.target.value)}
                              className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center"
                            />
                            {customization.name && (
                              <p className="mt-2 text-lg font-semibold text-gray-900">{customization.name}</p>
                            )}
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-2xl p-6">
                          <h4 className="text-xl font-bold text-gray-900 mb-4">Customization Options</h4>
                          
                          <div className="mb-6">
                            <div className="flex items-center mb-3">
                              <Palette className="h-5 w-5 text-purple-600 mr-2" />
                              <h5 className="text-lg font-semibold text-gray-900">Color Scheme</h5>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'].map((color) => (
                                <button
                                  key={color}
                                  className={`w-10 h-10 rounded-full border-2 transition-all ${
                                    customization.color === color ? 'border-gray-800 scale-110' : 'border-gray-300'
                                  }`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => handleCustomization('color', color)}
                                />
                              ))}
                            </div>
                          </div>

                          <div className="mb-6">
                            <div className="flex items-center mb-3">
                              <Shirt className="h-5 w-5 text-purple-600 mr-2" />
                              <h5 className="text-lg font-semibold text-gray-900">Accessories</h5>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              {accessories.map((accessory) => (
                                <button
                                  key={accessory.id}
                                  className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center ${
                                    customization.accessories.includes(accessory.id)
                                      ? 'border-purple-500 bg-purple-100'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  onClick={() => handleAccessoryToggle(accessory.id)}
                                >
                                  <span className="text-xl mb-1">{accessory.icon}</span>
                                  <span className="text-xs text-gray-700">{accessory.name}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={addToCart}
                              className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
                            >
                              <ShoppingCart className="h-5 w-5 mr-2" />
                              Add to Cart - ${selectedBase.price}
                            </button>
                            <button
                              onClick={closeModal}
                              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </main>
          </PageTransition>
        } />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </AnimatePresence>
  );
};

export default AnimatedRoutes;
